/* Public appointment response page — iter86.
 * Route: /appt-response/:token?r=accept|decline
 * Parent clicks the button in the reminder email/SMS → this page hits the
 * public API and shows a confirmation card. No login required. */
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, XCircle, Warning } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AppointmentResponse() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const r = params.get('r') === 'decline' ? 'decline' : 'accept';
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/public/appt-response/${token}`, { params: { r } });
        setResult(data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Lien invalide ou expiré');
      } finally { setLoading(false); }
    })();
  }, [token, r]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <p className="text-[#94A3B8]">Enregistrement de votre réponse…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-3"
                          data-testid="appt-error-card">
            <Warning size={48} className="mx-auto text-red-600" weight="duotone" />
            <h1 className="text-xl font-outfit font-bold">Lien inaccessible</h1>
            <p className="text-sm text-[#64748B]">{error}</p>
            <p className="text-xs text-[#94A3B8]">
              Le lien du rappel n'est valide que 3 jours. Contactez l'école
              directement si nécessaire.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isAccept = result.response === 'accept';
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center space-y-3"
                        data-testid={isAccept ? 'appt-confirm-card' : 'appt-decline-card'}>
          {isAccept
            ? <CheckCircle size={64} weight="duotone"
                              className="mx-auto text-emerald-600" />
            : <XCircle size={64} weight="duotone"
                         className="mx-auto text-red-600" />}
          <h1 className={`text-2xl font-outfit font-bold ${
            isAccept ? 'text-emerald-700' : 'text-red-700'
          }`}>
            {isAccept ? 'Merci — c\'est confirmé !' : 'Réponse enregistrée'}
          </h1>
          <p className="text-sm text-[#0F172A]">
            {isAccept
              ? <>Votre rendez-vous avec <strong>{result.teacher_name}</strong>{' '}
                    est confirmé pour <strong>{result.date} à {result.time}</strong>{' '}
                    concernant <strong>{result.student_name}</strong>.</>
              : <>Vous avez indiqué ne pas pouvoir venir au RDV du{' '}
                    <strong>{result.date} à {result.time}</strong>. L'école a été
                    prévenue. N'hésitez pas à proposer un autre créneau.</>}
          </p>
          <p className="text-xs text-[#64748B]">
            {isAccept
              ? 'L\'enseignant a été notifié. Vous pouvez fermer cette page.'
              : 'Contactez l\'équipe pédagogique pour reprogrammer.'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
